import React, { useMemo, useState } from 'react';

import { cn, iaRa } from 'lib/helpers/utility';

import { availableTimes as defaultAvailableTimes } from 'components/practice/Settings/components/Calendar/components/AvailableTime/lib';
import { isDayAvailable } from 'components/practice/Settings/components/PracticeDetails/lib/workingHoursUtils';
import SectionList from 'components/practice/UserPreferences/components/SectionList';
import {
  DEFAULT_END,
  DEFAULT_START,
  DayCard,
  activeStyle,
  inactiveStyle
} from 'components/shared/AvailabilityTime';

const AvailabilityForm = ({ values, setFieldValue }) => {
  const [expandedDay, setExpandedDay] = useState(null);

  const availableTimes = useMemo(
    () => values?.availableTimes || defaultAvailableTimes,
    [values?.availableTimes]
  );

  const updateAvailableTimes = (updater) => {
    const next = updater(availableTimes);
    setFieldValue('availableTimes', next);
  };

  const handleToggleType = (dayName, typeKey, enabled) => {
    updateAvailableTimes((prev) =>
      iaRa(prev).map((day) => {
        if (day.day !== dayName) return day;

        const updated = { ...day, [typeKey]: { ...(day[typeKey] || {}) } };

        if (enabled) {
          const hasTimes = updated[typeKey].starts_at != null && updated[typeKey].ends_at != null;
          if (!hasTimes) {
            updated[typeKey].starts_at = DEFAULT_START;
            updated[typeKey].ends_at = DEFAULT_END;
          }
        } else {
          updated[typeKey].starts_at = null;
          updated[typeKey].ends_at = null;
        }

        return updated;
      })
    );

    if (typeKey === 'virtual' && enabled) {
      setExpandedDay(dayName);
    }
  };

  const handleDayClick = (dayName) => {
    const dayData = availableTimes.find((d) => d.day === dayName);
    const isAvailable = isDayAvailable(dayData);

    if (isAvailable) {
      updateAvailableTimes((prev) =>
        prev.map((day) => {
          if (day.day !== dayName) return day;
          const updated = {
            ...day,
            location: { starts_at: null, ends_at: null },
            virtual: { starts_at: null, ends_at: null }
          };
          delete updated.break;
          return updated;
        })
      );
      if (expandedDay === dayName) setExpandedDay(null);
    } else {
      updateAvailableTimes((prev) =>
        prev.map((day) => {
          if (day.day !== dayName) return day;
          return {
            ...day,
            location: { ...(day.location || {}), starts_at: DEFAULT_START, ends_at: DEFAULT_END }
          };
        })
      );
      setExpandedDay(dayName);
    }
  };

  const availableWeekDays = iaRa(availableTimes).map((day) => ({
    day: day.day,
    available: isDayAvailable(day)
  }));

  const availableTimesRender = iaRa(availableTimes).map((day) => ({
    ...day,
    available: isDayAvailable(day)
  }));

  return (
    <SectionList
      title="Availability"
      description="Set the staff member's working days to manage availability.">
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          {availableWeekDays.map((day) => (
            <div
              key={day.day}
              onClick={() => handleDayClick(day.day)}
              className={cn(
                'flex h-[40px] w-[48px] cursor-pointer items-center justify-center rounded-md border text-sm font-500 shadow-sm',
                day.available ? activeStyle : inactiveStyle
              )}>
              {day.day.slice(0, 3)}
            </div>
          ))}
        </div>
        {availableTimesRender.map((day) => (
          <DayCard
            key={day.day}
            day={day}
            expandedDay={expandedDay}
            setExpandedDay={setExpandedDay}
            handleToggleType={handleToggleType}
            updateAvailableTimes={updateAvailableTimes}
          />
        ))}
      </div>
    </SectionList>
  );
};

export default AvailabilityForm;
